interface PhotoExpirationBannerProps {
  photoCount: number;
  daysRemaining: number;
  onKeepPhotos: () => void;
  onDismiss: () => void;
  extending: boolean;
}

/**
 * Warning banner shown when diagnosis photos are expiring within 7 days.
 * Provides options to extend retention or dismiss for the session.
 */
export function PhotoExpirationBanner({
  photoCount,
  daysRemaining,
  onKeepPhotos,
  onDismiss,
  extending,
}: PhotoExpirationBannerProps) {
  const daysText = daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;
  const photoText = photoCount === 1 ? 'A diagnosis photo' : `Diagnosis photos for ${photoCount} plants`;

  return (
    <div
      className="bg-warning/10 border-b border-warning/20 px-4 py-3"
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-lg mx-auto flex items-start gap-3">
        {/* Warning icon */}
        <div
          className="flex-shrink-0 w-6 h-6 rounded-full bg-warning/20 flex items-center justify-center mt-0.5"
          aria-hidden="true"
        >
          <span className="text-warning text-sm">!</span>
        </div>

        {/* Message */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-warning">
            <span className="font-medium">{photoText}</span> will be deleted in {daysText}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex-shrink-0 flex items-center gap-2">
          <button
            onClick={onKeepPhotos}
            disabled={extending}
            className="min-h-[36px] px-3 py-1.5 text-sm font-medium text-white bg-warning rounded-md hover:bg-warning/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {extending ? 'Extending...' : photoCount === 1 ? 'Keep Photo' : 'Keep All'}
          </button>
          <button
            onClick={onDismiss}
            disabled={extending}
            className="min-h-[36px] min-w-[36px] p-2 text-warning hover:text-warning hover:bg-warning/20 rounded-md disabled:opacity-50 transition-colors"
            aria-label="Dismiss warning"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      </div>
    </div>
  );
}
